import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Skeleton,
  FormHelperText,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAuthUser } from "react-auth-kit";
import moment from "moment";
import {
  checkSubDomainAccess,
  getSubDomainAccessStatus,
} from "../../../../components/RolePermissionAccess/RolePermissionAccess";
import TableSkeleton from "../../../../components/Skeletons/TableSkeleton";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SaveBtn from "../../../../components/Buttons/SaveBtn";
import InactiveAccountPopup from "../../../../components/CustomPopupBilling/InactiveAccountPopup";

const CustomTableCell = styled(TableCell)(({ theme }) => ({
  padding: "0px 12px",
  fontSize: "12px",
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "@media (max-width: 600px)": {
    fontSize: "12px",
  },
  "@media only screen and (min-width: 992px)": {
    fontSize: "12px",
  },
  "@media only screen and (min-width: 1200px)": {
    fontSize: "14px",
  },
}));

const CustomTableHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 12px",
  fontSize: "14px",
  color: "#fff",
  background: "#073E53",
  textTransform: "capitalize",
  fontWeight: "400",
}));

export default function Fees(props: any) {
  const { GetTransactionList, loading, transactionData } = props;
  const auth = useAuthUser();
  const authUser = auth();
  const { userById } = useSelector((state: any) => state.userDetails);
  const branches = useSelector((state: any) => state.branch.branchList);

  const hasAllBranchAccess = getSubDomainAccessStatus(
    "GLOBAL_PERMISSIONS",
    "BRANCH_ACCESS",
    "ALL_BRANCHES",
    userById?.rolePermission
  );

  const hasLeadCardPermission = checkSubDomainAccess(
    "NURTURE_BOARD",
    "LEAD_CARD",
    userById?.rolePermission
  );

  const [columns, setColumns] = useState([
    { id: "hash", label: "#", minWidth: 30, align: "inherit", show: true },
    {
      id: "name",
      label: "Student Name",
      minWidth: 170,
      align: "inherit",
      show: true,
    },
    {
      id: "course",
      label: "Course Name",
      minWidth: 200,
      align: "inherit",
      show: true,
    },
    {
      id: "batch",
      label: "Batch",
      minWidth: 200,
      align: "inherit",
      show: true,
    },
    {
      id: "branch",
      label: "Branch",
      minWidth: 170,
      align: "left",
      show: true,
    },
    {
      id: "transactionAmount",
      label: "Transaction Amount",
      minWidth: 180,
      align: "left",
      show: true,
    },
    {
      id: "transactionDate",
      label: "Transaction Date & Time",
      minWidth: 200,
      align: "left",
      show: true,
    },
    {
      id: "status",
      label: "status",
      minWidth: 150,
      align: "left",
      show: true,
    },
    {
      id: "paymentMode",
      label: "Payment Mode",
      minWidth: 150,
      align: "left",
      show: true,
    },
    {
      id: "type",
      label: "Type",
      minWidth: 150,
      align: "left",
      show: true,
    },
  ]);

  const [transactionList, setTransactionList] = useState<any>();

  const handleSelectCurrency = (id: string) => {
    if (id !== "") {
      const currency = branches
        ?.filter((branch: any) => branch.id == id)
        ?.map((item: any) => item.currency);
      return currency && currency[0] ? currency[0] : "₹";
    } else return "₹";
  };

  const [endDate, setEndDate] = React.useState<number>(dayjs().valueOf());
  const [startDate, setStartDate] = React.useState<number>(
    dayjs().subtract(30, "day").valueOf()
  );
  const [endDateError, setEndDateError] = React.useState<string>("");
  const [date, setDate] = React.useState<any>({
    startDate: dayjs().valueOf(),
    endDate: dayjs().subtract(30, "day").valueOf(),
  });

  const handleDateChange = (date: any, id: string) => {
    if (date !== null) {
      const dateMilliseconds = date.unix() * 1000;

      if (id === "startDate") {
        const startOfDay = dayjs(dateMilliseconds).startOf("day").valueOf();
        setStartDate(startOfDay);

        if (startOfDay > endDate) {
          setEndDateError("End date should be after start date");
        } else if (startOfDay === dayjs(endDate).startOf("day").valueOf()) {
          setEndDateError("Start date and end date cannot be the same");
        } else {
          setEndDateError("");
        }
      } else if (id === "endDate") {
        const endOfDay = dayjs(dateMilliseconds).endOf("day").valueOf();
        setEndDate(endOfDay);

        if (startDate > endOfDay) {
          setEndDateError("End date should be after start date");
        } else if (
          dayjs(startDate).startOf("day").valueOf() ===
          dayjs(endOfDay).startOf("day").valueOf()
        ) {
          setEndDateError("Start date and end date cannot be the same");
        } else {
          setEndDateError("");
        }
      }
    }
  };

  const handleApply = () => {
    const userFromLocalStorage = authUser?.institute?.[0]?.instituteDetails;
    const body = {
      reportType: "TRANSACTION",
      instituteId: userFromLocalStorage?.id,
      generateReports: true,
      batchId: [],
      branchId: hasAllBranchAccess ? [] : [authUser?.institute?.[0]?.branchId],
      courseId: [],
      paymentStatus: [],
      paymentMode: [],
      date: {
        type: "CUSTOM",
        startDate: startDate,
        endDate: endDate,
      },
    };

    GetTransactionList(body);
  };

  useEffect(() => {
    const userFromLocalStorage = authUser?.institute?.[0]?.instituteDetails;

    const body = {
      reportType: "TRANSACTION",
      instituteId: userFromLocalStorage?.id,
      generateReports: true,
      batchId: [],
      branchId: hasAllBranchAccess ? [] : [authUser?.institute?.[0]?.branchId],
      courseId: [],
      paymentStatus: [],
      paymentMode: [],
      date: {
        type: "CUSTOM",
        endDate: dayjs().valueOf(),
        startDate: dayjs().subtract(30, "day").valueOf(),
      },
    };
    GetTransactionList(body);
  }, []);

  useEffect(() => {
    if (transactionData) {
      setTransactionList(transactionData?.result);
    }
  }, [transactionData]);

  const instituteData = JSON.parse(localStorage.getItem("ACTIVE_INSTITUTE")!);
  const accountStatus = instituteData?.billingStatus;

  return (
    <div
      style={{
        padding: "14px 16px",
        overflowY: "hidden",
        minHeight: "calc(100vh - 64px)",
        maxHeight: "calc(100vh - 64px)",
        position: "relative",
        background: "rgb(245, 245, 245)",
      }}
    >
      {accountStatus === "INACTIVE" && (
        <InactiveAccountPopup type={accountStatus} />
      )}

      <div
        style={{
          background: "#fff",
          padding: "22px 30px 22px 30px",
          width: "100%",
          border: "1px solid #E9E9E9",
          borderRadius: "8px",
          overflowY: "auto",
          minHeight: "calc(100vh - 90px)",
          maxHeight: "calc(100vh - 90px)",
        }}
      >
        <div>
          {transactionList == null ? (
            <Skeleton
              animation="wave"
              variant="rectangular"
              width="250px"
              height={38}
              style={{
                borderRadius: "2px",
                marginRight: "10px",
                marginBottom: "16px",
                marginTop: "16px",
              }}
            />
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    margin: "0px",
                    color: "#142C44",
                    fontSize: "18px",
                    fontFamily: "Poppins",
                    fontWeight: "500",
                    marginTop: "10px",
                  }}
                >
                  Transactions
                </div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "150px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#667085B2",
                          marginBottom: "5PX",
                        }}
                      >
                        Start Date
                      </div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          sx={{
                            minHeight: "20px",
                            ".MuiInputBase-root": {
                              fontSize: "12px",
                              minHeight: "20px",
                              fontWeight: "600",
                            },
                            ".MuiFormLabel-root": {
                              lineHeight: "1",
                            },
                            ".MuiSvgIcon-root": {
                              fontSize: "18px",
                            },
                            ".MuiInputBase-input": {
                              padding: "10px 14px",
                            },
                          }}
                          format="DD - MMM - YY"
                          onChange={(date) =>
                            handleDateChange(date, "startDate")
                          }
                          value={startDate ? dayjs(startDate) : null}
                          disableFuture
                        />
                      </LocalizationProvider>
                    </div>
                    <div style={{ width: "150px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#667085B2",
                          marginBottom: "5PX",
                        }}
                      >
                        End Date
                      </div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          sx={{
                            minHeight: "20px",
                            ".MuiInputBase-root": {
                              fontSize: "12px",
                              minHeight: "20px",
                              fontWeight: "600",
                            },
                            ".MuiFormLabel-root": {
                              lineHeight: "1",
                            },
                            ".MuiSvgIcon-root": {
                              fontSize: "18px",
                            },
                            ".MuiInputBase-input": {
                              padding: "10px 14px",
                            },
                          }}
                          format="DD - MMM - YY"
                          onChange={(date) => handleDateChange(date, "endDate")}
                          value={endDate ? dayjs(endDate) : null}
                          disableFuture
                        />
                      </LocalizationProvider>
                    </div>
                    <SaveBtn
                      buttonText="Apply"
                      disabled={endDateError !== "" || loading}
                      onClick={handleApply}
                      loader={loading}
                    />
                  </div>
                  {endDateError !== "" && (
                    <FormHelperText error={true}>{endDateError}</FormHelperText>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            minHeight: "calc(100vh - 210px)",
            overflowY: "hidden",
          }}
        >
          {loading ? (
            <TableSkeleton />
          ) : (
            <>
              <div
                style={{
                  border: "1px solid #EAECF0",
                  borderRadius: "8px",
                  minHeight: "calc(100vh - 210px)",
                  position: "relative",
                  overflowY: "hidden",
                  marginTop: "5px",
                }}
              >
                {transactionList?.length > 0 ? (
                  <TableContainer
                    sx={{
                      maxHeight: "calc(100vh - 215px)",
                      minHeight: "calc(100vh - 215px)",
                      overflowY: "auto",
                    }}
                  >
                    <Table stickyHeader aria-label="sticky table">
                      <TableHead>
                        <TableRow
                          sx={{
                            height: "49px",
                          }}
                        >
                          {columns.map((column: any) => {
                            return (
                              <>
                                {column.show && (
                                  <CustomTableHeaderCell
                                    key={column.id}
                                    align={column?.align}
                                    style={{ minWidth: column.minWidth }}
                                  >
                                    {column.label}
                                  </CustomTableHeaderCell>
                                )}
                              </>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactionList?.length > 0 &&
                          transactionList?.map((student: any, index: any) => {
                            return (
                              <TableRow
                                role="checkbox"
                                tabIndex={-1}
                                key={index}
                                sx={{
                                  padding: "0px",
                                  height: "72px",
                                  cursor: "pointer",
                                }}
                                className="student_table_body"
                              >
                                <CustomTableCell>{index + 1}</CustomTableCell>

                                {columns[1].show && (
                                  <CustomTableCell sx={{}}>
                                    <div>
                                      <p
                                        style={{
                                          margin: "0px",
                                          fontSize: "12px",
                                          fontWeight: "400",
                                          textTransform: "capitalize",
                                        }}
                                      >
                                        {student?.studentFirstName}{" "}
                                        {student?.studentLastName}
                                      </p>
                                    </div>
                                  </CustomTableCell>
                                )}

                                {columns[2].show && (
                                  <CustomTableCell sx={{}}>
                                    <Box
                                      style={{
                                        color: "rgba(102, 112, 133, 1)",
                                      }}
                                    >
                                      {student?.courseName}
                                    </Box>
                                  </CustomTableCell>
                                )}

                                {columns[3].show && (
                                  <CustomTableCell sx={{}}>
                                    <>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <p
                                          style={{
                                            margin: "0px",
                                            fontSize: "13px",
                                            fontWeight: "400",
                                          }}
                                        >
                                          {student?.batchName}
                                        </p>
                                      </Box>
                                    </>
                                  </CustomTableCell>
                                )}

                                {columns[4].show && (
                                  <CustomTableCell sx={{}}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: "0px",
                                          textTransform: "capitalize",
                                          fontSize: "13px",
                                          fontWeight: "400",
                                        }}
                                      >
                                        {student?.branchName}
                                      </p>
                                    </Box>
                                  </CustomTableCell>
                                )}

                                {columns[5].show && (
                                  <CustomTableCell sx={{}}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: "0px",
                                          textTransform: "capitalize",
                                          fontSize: "13px",
                                          fontWeight: "400",
                                          fontFamily: "Roboto",
                                        }}
                                      >
                                        {handleSelectCurrency(
                                          student?.branchId
                                        )}
                                        {student?.amountReceived}
                                      </p>
                                    </Box>
                                  </CustomTableCell>
                                )}

                                {columns[6].show && (
                                  <CustomTableCell
                                    align="left"
                                    sx={{ alignItems: "center" }}
                                  >
                                    <p
                                      style={{
                                        margin: "0px",
                                        textAlign: "center",
                                        fontFamily: "Poppins",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        lineHeight: "18px",
                                        display: "inline-flex",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {moment(student?.paymentDate).format(
                                        "DD/MM/YYYY hh:mm A"
                                      )}
                                    </p>
                                  </CustomTableCell>
                                )}

                                {columns[7].show && (
                                  <CustomTableCell
                                    align="left"
                                    sx={{ alignItems: "center" }}
                                  >
                                    <p
                                      style={{
                                        margin: "0px",
                                        textAlign: "center",
                                        fontFamily: "Poppins",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        lineHeight: "18px",
                                        display: "inline-flex",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {student?.paymentStatus}
                                    </p>
                                  </CustomTableCell>
                                )}

                                {columns[8].show && (
                                  <CustomTableCell
                                    align="left"
                                    sx={{ alignItems: "center" }}
                                  >
                                    <p
                                      style={{
                                        margin: "0px",
                                        fontSize: "143x",
                                        fontWeight: "500",
                                        alignItems: "center",
                                        gap: "2px",
                                      }}
                                    >
                                      {student?.paymentSource
                                        ? student?.paymentSource
                                        : "-"}
                                    </p>
                                  </CustomTableCell>
                                )}

                                {columns[9].show && (
                                  <CustomTableCell
                                    sx={{ alignItems: "center" }}
                                  >
                                    <div
                                      style={{
                                        color:
                                          student?.paymentMode === "ONLINE"
                                            ? "rgba(20, 198, 154, 1)"
                                            : "#000",
                                        fontFamily: "Poppins",
                                        fontSize: "11px",
                                        fontWeight: "400",
                                        lineHeight: "20px",
                                        border:
                                          student?.paymentMode === "ONLINE"
                                            ? "1px solid rgba(20, 198, 154, 1)"
                                            : "1px solid rgba(102, 112, 133, 0.8)",
                                        textAlign: "center",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      {student?.paymentMode === "ONLINE"
                                        ? "Nurture Pay"
                                        : "Offline"}
                                    </div>
                                  </CustomTableCell>
                                )}
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <>
                    {transactionList?.length <= 0 ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "absolute",
                          fontSize: "14px",
                          color: "#667085",
                          fontWeight: "600",
                        }}
                      >
                        No data Available
                      </div>
                    ) : (
                      <TableSkeleton />
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
